import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { ThemeContext } from "./theme-context";
import type { ResolvedTheme, ThemePreference } from "./theme-context";

/* PrimeReact ships one compiled stylesheet per theme, so dark mode means
   swapping the file rather than re-declaring every component rule. Vite emits
   each as its own asset and hands us back the URL. */
import darkThemeUrl from "primereact/resources/themes/lara-dark-blue/theme.css?url";
import lightThemeUrl from "primereact/resources/themes/lara-light-blue/theme.css?url";

const STORAGE_KEY = "ctf-theme";
const LINK_ID = "prime-theme";

function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "light";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {
    /* Safari private mode and similar: fall through to the default. */
  }
  return "light";
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference;
}

/** Point the PrimeReact stylesheet at the matching compiled theme. */
function applyPrimeTheme(theme: ResolvedTheme) {
  if (typeof document === "undefined") return;
  const href = theme === "dark" ? darkThemeUrl : lightThemeUrl;
  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.id = LINK_ID;
    link.rel = "stylesheet";
    /* Ahead of our own bundle so token overrides always win the cascade. */
    document.head.insertBefore(link, document.head.firstChild);
  }
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);
}

function applyTheme(theme: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  applyPrimeTheme(theme);

  /* Keeps the mobile browser chrome in step with the page. */
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "dark" ? "#080d18" : "#f4f7fd");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredPreference);
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(preference));

  useEffect(() => {
    const next = resolve(preference);
    setTheme(next);
    applyTheme(next);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Not being able to persist the choice shouldn't break the toggle. */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
